import { useMemo, useState } from 'react'
import { Button, Card, Kpi, PageHead, Skeleton, StateBlock } from '@w7/ui'
import { EXPOSURE_SORTS, useExposure, useSession } from '@/app/queries'
import { AsOf, StaleProjection, exportCsv } from '@/app/bits'
import { count, isoDay, lll, money, moneyCompact } from '@/lib/format'
import { ROLE_LABEL } from '@/data/types'
import { ExposureGrid } from './ExposureGrid'
import { ExposureToolbar, type Density } from './ExposureToolbar'
import { exposureQueryOf, useExposureView } from './query'
import { shapeEvent } from './runners'
import type { EventExposure } from '@/data/types'

/**
 * Exposure — the risk desk's landing screen.
 *
 * The product being replaced rendered this as a stack of per-event tables whose
 * columns were "Net Exposure / 1 / X / 2 / Fancy / Fancy Exposure", every cell
 * reading 0. Nothing said which outcome hurt, the header strip was repeated
 * inside every event and never named the runners it was heading, a fancy line
 * cost a full-width row to show one number, nothing was sortable, nothing was a
 * link, and there was no total. So the page could not answer the only question
 * anyone opens it to ask.
 *
 * Here the worst case is the headline, the events are ordered by it, every
 * market is one dense row whose runner positions sit in columns that line up
 * across the whole page, and the filter set that produced the number lives in
 * the URL beside it.
 */

export function ExposurePage() {
  const { view, set, clearFilters, isFiltered, filterLabels } = useExposureView()
  const [density, setDensity] = useState<Density>('compact')

  const { data: session } = useSession()
  const query = useMemo(() => exposureQueryOf(view), [view])
  const { data, isLoading, isError, error, refetch, isFetching, isPlaceholderData } =
    useExposure(query)

  const events = useMemo<EventExposure[]>(() => data?.value ?? [], [data])

  const totals = useMemo(
    () =>
      events.reduce(
        (acc, event) => ({
          worstCase: acc.worstCase + event.worstCase,
          bestCase: acc.bestCase + event.bestCase,
          // In-play risk is the risk you cannot walk away from: you can stop
          // taking bets on a market that has not started, but you cannot unwind
          // one that is running.
          liveWorst: acc.liveWorst + (event.inPlay ? event.worstCase : 0),
          liveEvents: acc.liveEvents + (event.inPlay ? 1 : 0),
          bets: acc.bets + event.betCount,
          // memberCount is distinct within an event but not across them, so this
          // is positions, not people. The label says so rather than overstating.
          positions: acc.positions + event.memberCount,
          markets: acc.markets + event.markets.length,
          events: acc.events + 1,
        }),
        {
          worstCase: 0,
          bestCase: 0,
          liveWorst: 0,
          liveEvents: 0,
          bets: 0,
          positions: 0,
          markets: 0,
          events: 0,
        },
      ),
    [events],
  )

  const fancyLines = useMemo(
    () =>
      events.reduce(
        (sum, event) =>
          sum +
          event.markets.filter(
            (market) => market.marketType === 'Fancy' || market.marketType === 'Premium Fancy',
          ).length,
        0,
      ),
    [events],
  )

  const sortLabel = EXPOSURE_SORTS.find((entry) => entry.id === view.sort)?.label ?? 'Worst case first'

  const head = (
    <PageHead
      title="Exposure"
      subtitle="Every open position beneath you, ordered by what it could cost."
      actions={
        <span className="w7-row">
          {isPlaceholderData ? <span className="w7-asof">Updating…</span> : null}
          <AsOf at={data?.asOf} polls />
        </span>
      }
    />
  )


  if (isLoading) {
    return (
      <>
        {head}
        <ExposureSkeleton />
      </>
    )
  }

  if (isError || !data) {
    return (
      <>
        {head}
        <StateBlock
          icon="exposure"
          title="No data found"
          actions={
            <Button variant="secondary" icon="refresh" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        >
          {(error as Error)?.message ||
            'Exposure could not be reached. The page stays open — nothing is invented.'}
        </StateBlock>
      </>
    )
  }

  // A stale projection replaces every figure on this page rather than sitting
  // beside them: bet acceptance is already failing conservatively, so any number
  // shown here would be describing a book that has moved on. Not even a KPI,
  // not even a dimmed one.
  if (data?.stale) {
    return (
      <>
        {head}
        <StaleProjection asOf={data.asOf} />
      </>
    )
  }

  if (isLoading || !data) {
    return (
      <>
        {head}
        <ExposureSkeleton />
      </>
    )
  }

  return (
    <>
      {head}

      {/* The toolbar sits ABOVE the KPI strip because the KPIs describe the
          filtered set: you have to see the control that produced a number
          before you see the number. */}
      <ExposureToolbar
        view={view}
        set={set}
        clearFilters={clearFilters}
        isFiltered={isFiltered}
        filterLabels={filterLabels}
        density={density}
        onDensityChange={setDensity}
        share={session?.account.share.sportsbook}
        onRefresh={() => refetch()}
        isFetching={isFetching}
        onExport={() => exportCsv(`w7-exposure-${view.basis}-${isoDay(Date.now())}.csv`, csvRows(events, view.basis))}
        canExport={events.length > 0}
        counts={{ events: totals.events, markets: totals.markets, fancyLines }}
      />

      {/* ------------------------------------------------------- headline -- */}
      <div className="w7-grid w7-grid-5">
        <Kpi
          label="Worst case"
          value={moneyCompact(totals.worstCase)}
          exact={money(totals.worstCase)}
          tone={totals.worstCase < 0 ? 'loss' : 'neutral'}
          accent
          meta={`Across ${count(totals.events)} ${totals.events === 1 ? 'event' : 'events'}`}
        />
        <Kpi
          label="Best case"
          value={moneyCompact(totals.bestCase)}
          exact={money(totals.bestCase)}
          tone={totals.bestCase > 0 ? 'profit' : 'neutral'}
          meta="If every market lands the book's way"
        />
        <Kpi
          label="In play"
          value={moneyCompact(totals.liveWorst)}
          exact={money(totals.liveWorst)}
          tone={totals.liveWorst < 0 ? 'loss' : 'neutral'}
          meta={`${count(totals.liveEvents)} of ${count(totals.events)} events are live`}
          onClick={() => set({ live: !view.live })}
        />
        <Kpi
          label="Open bets"
          value={count(totals.bets)}
          meta={`In ${count(totals.markets)} ${totals.markets === 1 ? 'market' : 'markets'}`}
        />
        {/* The leaf tier always renders from ROLE_LABEL — this console never
            says "Player", in a label or anywhere else. */}
        <Kpi
          label={`${ROLE_LABEL.player} positions`}
          value={count(totals.positions)}
          meta="Counted once per event, so one member in two events counts twice"
        />
      </div>

      <div style={{ marginBottom: 'var(--w7-s5)' }}>
        <p className="w7-card-sub" style={{ maxWidth: '72ch' }}>
          Worst case adds up the losing outcome of every market as though they all settled against
          the book at the same moment. That is deliberately conservative — it is the number credit
          decisions are made against, so it is never allowed to read better than reality. Inside a
          single market the outcomes are alternatives, not additions: the 1, X and 2 cells on a row
          are three different futures, and the row&rsquo;s worst case is the smallest of them, never
          their sum.
        </p>
        {/* A KPI that silently describes a filtered subset is how a desk ends up
            quoting the wrong number in a call. */}
        {isFiltered ? (
          <p className="w7-card-sub w7-dimmer" style={{ maxWidth: '72ch' }}>
            These figures describe the {count(totals.events)}{' '}
            {totals.events === 1 ? 'event' : 'events'} in view, not your whole book.
          </p>
        ) : null}
      </div>

      {/* ----------------------------------------------------------- grid -- */}
      {events.length === 0 ? (
        view.eventId ? (
          // The operator arrived by link and needs to know the LINK is stale,
          // not that the console is broken.
          <StateBlock
            icon="info"
            title="That event no longer has an open position"
            actions={
              <Button variant="secondary" onClick={() => set({ eventId: null })}>
                Show every event
              </Button>
            }
          >
            It settled, or every bet on it was voided, since this link was made.
          </StateBlock>
        ) : isFiltered ? (
          <StateBlock
            icon="search"
            title="No open positions match these filters"
            actions={
              <Button variant="secondary" onClick={clearFilters}>
                Clear filters
              </Button>
            }
          >
            Widen the search, or drop a sport, market type, in-play or threshold filter.
          </StateBlock>
        ) : (
          <StateBlock icon="exposure" title="Nothing is at risk right now">
            No member beneath you has an unsettled bet. Positions appear here the moment one is
            placed, and the page refreshes on its own while it is open.
          </StateBlock>
        )
      ) : (
        <ExposureGrid events={events} density={density} sortLabel={sortLabel} />
      )}
    </>
  )
}

/* -------------------------------------------------------------------- csv -- */

/**
 * One row per market, fancy lines included.
 *
 * `All runners` is populated for every market, so a seven-runner handicap
 * exports losslessly instead of being crammed into three numbered columns.
 * `Basis` repeats on every row: a spreadsheet that leaves its lens in the
 * filename gets emailed onward without it.
 */
function csvRows(events: EventExposure[], basis: 'my' | 'total') {
  const basisLabel = basis === 'my' ? 'My share' : 'Total book'
  return events.flatMap((event) => {
    const { rows, lines } = shapeEvent(event)
    return [...rows, ...lines].map(({ market, shape }) => {
      const sided = shape.kind === 'sided' ? shape : null
      return {
        Event: event.eventName,
        Sport: event.sport,
        Starts: lll(event.startsAt),
        'In play': event.inPlay ? 'Yes' : 'No',
        Market: market.marketName,
        'Market type': market.marketType,
        'Worst case': market.worstCase,
        'Best case': market.bestCase,
        'Runner 1': sided?.home?.name ?? '',
        'Runner 1 book': sided?.home?.net ?? '',
        X: sided?.draw?.name ?? '',
        'X book': sided?.draw?.net ?? '',
        'Runner 2': sided?.away?.name ?? '',
        'Runner 2 book': sided?.away?.net ?? '',
        'All runners': market.selections.map((s) => `${s.name}=${s.net}`).join('; '),
        Matched: market.matched,
        Unmatched: market.unmatched,
        Bets: market.betCount,
        Members: market.memberCount,
        Basis: basisLabel,
      }
    })
  })
}

/* --------------------------------------------------------------- skeleton -- */

/**
 * The loading state at the real geometry — toolbar chips, five tiles, then two
 * event groups of one group row and three market rows, at the grid's own row
 * heights. Nothing may jump when the projection lands.
 */
function ExposureSkeleton() {
  return (
    <>
      <Card style={{ marginBottom: 'var(--w7-s5)' }}>
        <div className="w7-col">
          <div className="w7-row w7-row-wrap">
            <Skeleton width={190} height={34} radius={999} />
            <Skeleton width={280} height={38} />
          </div>
          <div className="w7-row w7-row-wrap" style={{ gap: 6 }}>
            {[64, 72, 68, 60, 104, 122, 88].map((width, index) => (
              <Skeleton key={index} width={width} height={28} radius={999} />
            ))}
          </div>
        </div>
      </Card>

      <div className="w7-grid w7-grid-5" style={{ marginBottom: 'var(--w7-s5)' }}>
        {['Worst case', 'Best case', 'In play', 'Open bets', `${ROLE_LABEL.player} positions`].map(
          (label) => (
            <Kpi
              key={label}
              label={label}
              value={<Skeleton width={104} height={26} />}
              meta={<Skeleton width={132} height={12} />}
            />
          ),
        )}
      </div>

      <Card flush>
        <div className="w7-tablewrap">
          <table className="w7-table w7-expo" data-density="compact">
            <caption className="w7-sr">Loading open positions</caption>
            <thead>
              <tr>
                <th data-col="market">Event / market</th>
                <th data-col="worst" data-align="right">
                  Worst case ₹
                </th>
                <th data-col="best" data-align="right" data-hide-below={1080}>
                  Best case ₹
                </th>
                <th data-col="r1" data-align="right">
                  1
                </th>
                <th data-col="rx" data-align="right">
                  X
                </th>
                <th data-col="r2" data-align="right">
                  2
                </th>
                <th data-col="matched" data-align="right" data-hide-below={1280}>
                  Matched ₹
                </th>
                <th data-col="counts" data-align="right" data-hide-below={900}>
                  Bets / members
                </th>
              </tr>
            </thead>
            {[0, 1].map((group) => (
              <tbody key={group}>
                <tr className="w7-expo-group">
                  <th scope="rowgroup" data-col="market">
                    <Skeleton width={180} height={13} />
                  </th>
                  {[76, 68, 64, 64, 64, 68, 70].map((width, index) => (
                    <td key={index} data-align="right">
                      <Skeleton width={width} height={12} />
                    </td>
                  ))}
                </tr>
                {[0, 1, 2].map((row) => (
                  <tr key={row}>
                    <td data-col="market">
                      <Skeleton width={132} height={12} />
                    </td>
                    {[70, 62, 58, 58, 58, 62, 62].map((width, index) => (
                      <td key={index} data-align="right">
                        <Skeleton width={width} height={12} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      </Card>
    </>
  )
}
